/**
 * 项目总览页面
 * Feature: mobile-responsive-framework - 应用响应式框架
 * Feature: real-time-refresh - 实时数据刷新
 * Feature: project-centric-view - 项目为中心的视图
 * Feature: dual-view-mode - 双视图切换（卡片/表格/列表）
 * Feature: project-overview-modal - 项目概览弹窗
 */
import React, { useState, useEffect } from 'react';
import { Card, Typography, Alert, Space, Button, Modal, message, ConfigProvider } from 'antd';
import {
  ProjectOutlined,
  ClockCircleOutlined,
  ToolOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StatisticsService, DashboardFocusItem } from '../../services/statisticsService';
import { DashboardService, ProjectOverviewData } from '../../services/dashboardService';
import { ProjectService } from '../../services/projectService';
import { getBudgetDetail } from '../../services/budgetService';
import { Project } from '../../types';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { ResponsiveContainer } from '../../responsive/components/ResponsiveContainer';
import ProjectOverviewModal from '../../components/ProjectOverviewModal';
import ProjectForm from '../Project/ProjectForm';
import WorkTimeForm from '../WorkTime/WorkTimeForm';
import EquipmentUsageForm from '../Equipment/EquipmentUsageForm';
import MaterialUsageForm from '../Material/MaterialUsageForm';
import WorkerHistoryForm from '../../components/WorkerHistoryForm';
import AdditionDetailDrawer from '../../modules/progress/components/AdditionDetailDrawer';
import RectificationDetailDrawer from '../../modules/progress/components/RectificationDetailDrawer';
import BatchAdditionModal from '../../modules/progress/components/BatchAdditionModal';
import api from '../../services/api';
import DashboardTower from './DashboardTower';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  // 路由导航
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // 响应式配置
  const { isMobile } = useResponsiveConfig();

  // 项目概览弹窗状态
  const [overviewModalVisible, setOverviewModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectOverviewData | null>(null);
  const [projectFormVisible, setProjectFormVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingBudgetDetail, setEditingBudgetDetail] = useState<{
    revenueBreakdown?: any;
    revenueTotal?: number;
    budget?: number;
    budgetBreakdown?: any;
    budgetVersion?: number;
    budgetLocked?: boolean;
  } | null>(null);
  
  // 添加数据弹窗状态
  const [addDataModalVisible, setAddDataModalVisible] = useState(false);
  const [selectedProjectForAdd, setSelectedProjectForAdd] = useState<ProjectOverviewData | null>(null);

  // 工时表单弹窗状态
  const [workTimeFormVisible, setWorkTimeFormVisible] = useState(false);
  // 设备使用表单弹窗状态
  const [equipmentFormVisible, setEquipmentFormVisible] = useState(false);
  // 材料使用表单弹窗状态
  const [materialFormVisible, setMaterialFormVisible] = useState(false);
  const [selectedFocusItem, setSelectedFocusItem] = useState<DashboardFocusItem | null>(null);
  const [additionDrawerOpen, setAdditionDrawerOpen] = useState(false);
  const [rectificationDrawerOpen, setRectificationDrawerOpen] = useState(false);
  const [additionModalVisible, setAdditionModalVisible] = useState(false);
  const [additionSubcontractors, setAdditionSubcontractors] = useState<any[]>([]);

  // 点击项目卡片 - 打开概览弹窗
  const handleProjectClick = (project: ProjectOverviewData) => {
    setSelectedProject(project);
    setOverviewModalVisible(true);
  };

  // 关闭概览弹窗
  const handleCloseOverviewModal = () => {
    setOverviewModalVisible(false);
    setSelectedProject(null);
  };

  const refreshProjectRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['projects-overview', 'management-page'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
  };

  const handleEditProject = async (project: ProjectOverviewData) => {
    try {
      const [projectRes, budgetRes] = await Promise.all([
        ProjectService.getProjectById(project.id),
        getBudgetDetail(project.id),
      ]);
      if (projectRes.success && projectRes.data) {
        setEditingProject(projectRes.data);
        if (budgetRes.success && budgetRes.data) {
          setEditingBudgetDetail(budgetRes.data);
        } else {
          setEditingBudgetDetail(null);
        }
        setProjectFormVisible(true);
      } else {
        message.error(projectRes.message || '获取项目详情失败');
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || '获取项目详情失败');
    }
  };

  const handleProjectFormSuccess = () => {
    setProjectFormVisible(false);
    setEditingProject(null);
    setEditingBudgetDetail(null);
    refreshProjectRelatedQueries();
  };

  const handleProjectFormCancel = () => {
    setProjectFormVisible(false);
    setEditingProject(null);
    setEditingBudgetDetail(null);
  };

  // 导出项目数据
  const handleExport = async (project: ProjectOverviewData) => {
    try {
      message.loading({ content: '正在导出...', key: 'export' });
      navigate(`/reports/project-comprehensive?projectId=${project.id}&action=export`);
      message.success({ content: '已跳转到报表页面，请在报表页面进行导出', key: 'export' });
    } catch (error) {
      message.error({ content: '导出失败', key: 'export' });
    }
  };

  // 添加数据
  const handleAddData = (project: ProjectOverviewData) => {
    setSelectedProjectForAdd(project);
    setAddDataModalVisible(true);
  };

  // 添加数据选项
  const handleAddDataOption = (type: 'worktime' | 'equipment' | 'material') => {
    setAddDataModalVisible(false);
    if (!selectedProjectForAdd) return;

    // 关闭选择弹窗后直接打开对应的表单弹窗
    switch (type) {
      case 'worktime':
        setWorkTimeFormVisible(true);
        break;
      case 'equipment':
        setEquipmentFormVisible(true);
        break;
      case 'material':
        setMaterialFormVisible(true);
        break;
    }
  };

  // 获取Dashboard完整数据（使用聚合接口）
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchDashboardData = () => DashboardService.getDashboardData() as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchDashboardFocus = () => StatisticsService.getDashboardFocusStream() as any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard
  } = useQuery<any, any, any, any>({
    queryKey: ['dashboard', 'overview'],
    queryFn: fetchDashboardData,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const {
    data: dashboardFocusData,
    isLoading: dashboardFocusLoading,
    refetch: refetchDashboardFocus
  } = useQuery<any, any, any, any>({
    queryKey: ['dashboard', 'focus'],
    queryFn: fetchDashboardFocus,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (!projectId || !dashboardData?.projects?.length) return;

    const matchedProject = dashboardData.projects.find((project: any) => project.id === projectId || project._id === projectId);
    if (!matchedProject) {
      message.warning('未找到指定的项目，请检查项目ID');
      const next = new URLSearchParams(searchParams);
      next.delete('projectId');
      setSearchParams(next);
      return;
    }

    setSelectedProject(matchedProject);
    setOverviewModalVisible(true);

    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    setSearchParams(next);
  }, [searchParams, dashboardData?.projects, setSearchParams]);

  if (dashboardError) {
    return (
      <ResponsiveContainer maxWidth="full" padding="none">
        <Title level={2}>项目总览</Title>
        <Alert
          title="数据加载失败"
          description="无法连接到后端服务，请检查网络连接或联系管理员"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </ResponsiveContainer>
    );
  }

  const handleFocusItemClick = (item: DashboardFocusItem) => {
    setSelectedFocusItem(item);
    setAdditionDrawerOpen(item.type === 'addition');
    setRectificationDrawerOpen(item.type === 'rectification');
  };

  const handleFocusRefresh = async () => {
    await refetchDashboardFocus();
  };

  const fetchAdditionSubcontractors = async () => {
    const res = await api.get('/progress/subcontractors');
    if (res.success) setAdditionSubcontractors(res.data || []);
  };

  const handleOpenAdditionModal = async () => {
    await fetchAdditionSubcontractors();
    setAdditionModalVisible(true);
  };

  const handleBatchAdditionSuccess = async () => {
    setAdditionModalVisible(false);
    await refetchDashboardFocus();
  };

  return (
    <ResponsiveContainer maxWidth="full" padding="none">
      <DashboardTower
        projects={dashboardData?.projects || []}
        additions={dashboardFocusData?.additions || []}
        rectifications={dashboardFocusData?.rectifications || []}
        loading={dashboardLoading}
        onProjectClick={handleProjectClick}
        onAdditionClick={handleFocusItemClick}
        onRectificationClick={handleFocusItemClick}
        onAddProject={() => setProjectFormVisible(true)}
        onAddAddition={handleOpenAdditionModal}
        onAddRectification={() => navigate('/progress/rectifications')}
        onExport={() => { void 0; }}
        onRefresh={async () => { await refetchDashboard(); await refetchDashboardFocus(); }}
        summaryMetrics={{
          totalProjects: dashboardData?.overview?.totalProjects || 0,
          activeProjects: dashboardData?.overview?.activeProjects || 0,
          totalBudget: dashboardData?.overview?.totalBudget,
          totalActualCost: dashboardData?.overview?.totalCost || 0,
          totalBudgetUsage: dashboardData?.overview?.totalBudgetUsageRate != null ? dashboardData.overview.totalBudgetUsageRate * 100 : undefined,
          overBudgetCount: dashboardData?.overview?.overBudgetProjects,
          overdueCount: dashboardData?.overview?.overdueProjects,
          staleCount: dashboardData?.overview?.staleProjects,
          riskyCount: (dashboardData?.overview?.overBudgetProjects || 0) + (dashboardData?.overview?.staleProjects || 0),
          pendingApprovalCount: 0,
        }}
      />

      {/* 项目概览弹窗 */}
      <ProjectOverviewModal
        visible={overviewModalVisible}
        project={selectedProject}
        onClose={handleCloseOverviewModal}
        onExport={handleExport}
        onEditProject={handleEditProject}
        onDataChange={refreshProjectRelatedQueries}
      />

      <Modal
        open={projectFormVisible}
        onCancel={handleProjectFormCancel}
        footer={null}
        width={860}
        destroyOnHidden
        className="project-form-modal"
        styles={{ body: { overflowY: 'auto' } }}
      >
        <ProjectForm
          project={editingProject}
          budgetDetail={editingBudgetDetail}
          onSubmit={handleProjectFormSuccess}
          onCancel={handleProjectFormCancel}
        />
      </Modal>

      {/* 添加数据弹窗 */}
      <Modal
        title={`添加数据 - ${selectedProjectForAdd?.name || ''}`}
        open={addDataModalVisible}
        onCancel={() => setAddDataModalVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '16px 0' }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Button
              block
              size="large"
              icon={<ClockCircleOutlined />}
              onClick={() => handleAddDataOption('worktime')}
            >
              新增出工记录
            </Button>
            <Button
              block
              size="large"
              icon={<ToolOutlined />}
              onClick={() => handleAddDataOption('equipment')}
            >
              添加设备使用记录
            </Button>
            <Button
              block
              size="large"
              icon={<ShoppingOutlined />}
              onClick={() => handleAddDataOption('material')}
            >
              添加材料使用记录
            </Button>
          </Space>
        </div>
      </Modal>

      {/* 工时记录表单弹窗 */}
      <Modal
        title="新增出工记录"
        open={workTimeFormVisible}
        onCancel={() => setWorkTimeFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <WorkerHistoryForm
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setWorkTimeFormVisible(false);
            message.success('出工记录添加成功');
          }}
          onCancel={() => setWorkTimeFormVisible(false)}
        />
      </Modal>

      {/* 设备使用记录表单弹窗 */}
      <Modal
        title="添加设备使用记录"
        open={equipmentFormVisible}
        onCancel={() => setEquipmentFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <EquipmentUsageForm
          initialValues={null}
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setEquipmentFormVisible(false);
          }}
          onCancel={() => setEquipmentFormVisible(false)}
        />
      </Modal>

      {/* 材料使用记录表单弹窗 */}
      <Modal
        title="添加材料使用记录"
        open={materialFormVisible}
        onCancel={() => setMaterialFormVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <MaterialUsageForm
          initialValues={null}
          defaultProjectId={selectedProjectForAdd?.id}
          onSuccess={() => {
            setMaterialFormVisible(false);
          }}
          onCancel={() => setMaterialFormVisible(false)}
        />
      </Modal>

      <AdditionDetailDrawer
        open={additionDrawerOpen}
        additionId={selectedFocusItem?.type === 'addition' ? selectedFocusItem.id : undefined}
        onClose={() => setAdditionDrawerOpen(false)}
        onSuccess={handleFocusRefresh}
      />

      <RectificationDetailDrawer
        open={rectificationDrawerOpen}
        rectificationId={selectedFocusItem?.type === 'rectification' ? selectedFocusItem.id : undefined}
        onClose={() => setRectificationDrawerOpen(false)}
        onSuccess={handleFocusRefresh}
      />

      <BatchAdditionModal
        visible={additionModalVisible}
        subcontractors={additionSubcontractors}
        onClose={() => setAdditionModalVisible(false)}
        onSuccess={handleBatchAdditionSuccess}
      />
    </ResponsiveContainer>
  );
};

export default Dashboard;
